export const normalizeMetaValues = (metaValues) => {
  if (Array.isArray(metaValues)) {
    return [...new Set(metaValues.filter(Boolean))];
  }

  if (typeof metaValues === "string") {
    return [
      ...new Set(
        metaValues
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    ];
  }

  return [];
};

export const normalizeInboxThread = (thread) => ({
  ...thread,
  meta_values: normalizeMetaValues(thread?.meta_values),
});

export const hasThreadMeta = (thread, token) =>
  normalizeMetaValues(thread?.meta_values).includes(token);

export const setThreadMetaValue = (thread, token, enabled) => {
  const metaValues = normalizeMetaValues(thread?.meta_values);
  const nextMetaValues = enabled
    ? [...new Set([...metaValues, token])]
    : metaValues.filter((value) => value !== token);

  return { ...thread, meta_values: nextMetaValues };
};

export const threadMatchesFilter = (thread, filter = "") => {
  const metaValues = normalizeMetaValues(thread?.meta_values);

  if (filter === "all") return true;
  if (filter === "archived") return metaValues.includes("archived");
  if (filter === "unread") {
    return metaValues.includes("unread") && !metaValues.includes("archived");
  }
  if (filter === "priority") {
    return metaValues.includes("priority") && !metaValues.includes("archived");
  }

  return !metaValues.includes("archived");
};

const previewTimestamp = (thread) => {
  const timestamp = Date.parse(thread?.last_message_on);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const previewType = (thread) => String(thread?.last_msg_type || "")[0];

export const mergeInboxThreadSnapshot = (current, incoming) => {
  const normalizedIncoming = normalizeInboxThread(incoming);
  if (!current) return normalizedIncoming;

  const currentMessageId = current.last_message_id;
  const incomingMessageId = normalizedIncoming.last_message_id;
  const sameMessage =
    currentMessageId !== null &&
    currentMessageId !== undefined &&
    incomingMessageId !== null &&
    incomingMessageId !== undefined &&
    String(currentMessageId) === String(incomingMessageId);
  const bothMessageIdsPresent =
    currentMessageId !== null &&
    currentMessageId !== undefined &&
    incomingMessageId !== null &&
    incomingMessageId !== undefined;
  const samePreview =
    !bothMessageIdsPresent &&
    current.last_msg === normalizedIncoming.last_msg &&
    previewType(current) === previewType(normalizedIncoming);
  const currentTimestamp = previewTimestamp(current);
  const incomingTimestamp = previewTimestamp(normalizedIncoming);
  const currentIsNewer =
    currentTimestamp !== null &&
    (incomingTimestamp === null || currentTimestamp >= incomingTimestamp);

  if (sameMessage || samePreview || !currentIsNewer) {
    return normalizedIncoming;
  }

  return normalizeInboxThread({
    ...normalizedIncoming,
    last_message_id: current.last_message_id,
    last_msg: current.last_msg,
    last_message_on: current.last_message_on,
    last_msg_type: current.last_msg_type,
  });
};

export const mergeInboxPage = (
  current,
  page,
  {
    replace = false,
    position = "append",
    preserveIds = [],
    filter = "",
  } = {},
) => {
  const currentThreads = Array.isArray(current) ? current : [];
  const currentById = new Map(
    currentThreads.map((thread) => [
      String(thread.id),
      thread,
    ]),
  );
  const normalizedPage = (page || []).map((thread) =>
    mergeInboxThreadSnapshot(currentById.get(String(thread.id)), thread),
  );
  if (replace || !Array.isArray(current)) {
    const incomingIds = new Set(
      normalizedPage.map((thread) => String(thread.id)),
    );
    const idsToPreserve = new Set(
      (preserveIds || [])
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    );
    const preserved = currentThreads
      .filter(
        (thread) =>
          idsToPreserve.has(String(thread.id)) &&
          !incomingIds.has(String(thread.id)) &&
          threadMatchesFilter(thread, filter),
      )
      .map(normalizeInboxThread);

    return [...preserved, ...normalizedPage];
  }

  const incomingIds = new Set(
    normalizedPage.map((thread) => String(thread.id)),
  );
  const retained = current.filter(
    (thread) => !incomingIds.has(String(thread.id)),
  );
  if (position === "front") return [...normalizedPage, ...retained];

  const incomingById = new Map(
    normalizedPage.map((thread) => [String(thread.id), thread]),
  );
  const updatedCurrent = current.map(
    (thread) => incomingById.get(String(thread.id)) || thread,
  );
  const currentIds = new Set(current.map((thread) => String(thread.id)));
  return [
    ...updatedCurrent,
    ...normalizedPage.filter((thread) => !currentIds.has(String(thread.id))),
  ];
};

export const promoteInboxThread = (threads, threadId, message) => {
  const sourceThreads = Array.isArray(threads) ? threads : [];
  const index = sourceThreads.findIndex(
    (thread) => String(thread.id) === String(threadId),
  );
  if (index < 0) return sourceThreads;

  const current = sourceThreads[index];
  const promoted = normalizeInboxThread({
    ...current,
    last_message_id: message?.message_id ?? current.last_message_id,
    last_msg: message?.content ?? current.last_msg,
    last_message_on: message?.created_at ?? current.last_message_on,
    last_msg_type: (message?.sender_type || "")[0] || current.last_msg_type,
  });

  return [
    promoted,
    ...sourceThreads.filter((_, threadIndex) => threadIndex !== index),
  ];
};

const OUTBOUND_MESSAGE_TYPES = ["automated", "user", "recipient"];

export const applyRealtimeInboxMessage = (
  threads,
  threadId,
  message,
  { viewing = false, filter = "" } = {},
) => {
  const promoted = promoteInboxThread(threads, threadId, message);
  const found = promoted.some(
    (thread) => String(thread.id) === String(threadId),
  );
  if (!found) {
    return { threads: promoted, found: false, removed: false };
  }

  const senderType = String(message?.sender_type || "").toLowerCase();
  const shouldMarkUnread =
    !viewing && !OUTBOUND_MESSAGE_TYPES.includes(senderType);
  if (!viewing && !shouldMarkUnread) {
    return { threads: promoted, found: true, removed: false };
  }

  return updateThreadMetaInList(
    promoted,
    threadId,
    "unread",
    shouldMarkUnread,
    filter,
  );
};

export const updateThreadMetaInList = (
  threads,
  threadId,
  token,
  enabled,
  filter,
  { preserveMembership = false } = {},
) => {
  let found = false;
  let removed = false;

  const sourceThreads = Array.isArray(threads) ? threads : [];
  const nextThreads = sourceThreads.reduce((result, thread) => {
    if (String(thread.id) !== String(threadId)) {
      result.push(thread);
      return result;
    }

    found = true;
    const updated = setThreadMetaValue(thread, token, enabled);
    if (!preserveMembership && !threadMatchesFilter(updated, filter)) {
      removed = true;
      return result;
    }

    result.push(updated);
    return result;
  }, []);

  return { threads: nextThreads, found, removed };
};

export const extractThreadMetaTokens = (thread) => {
  if (Array.isArray(thread?.meta?.tokens)) return thread.meta.tokens;
  if (Array.isArray(thread?.meta)) {
    return thread.meta.map((item) => item?.token).filter(Boolean);
  }
  return normalizeMetaValues(thread?.meta_values);
};
